import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  useTheme,
  Button,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';

interface ActivityItem {
  id: string;
  type: 'upload' | 'download' | 'share' | 'delete' | 'edit' | 'star' | 'unstar' | 'create_folder' | 'settings_update' | 'avatar_upload' | 'avatar_delete' | 'folder_rename' | 'file_rename' | 'folder_create' | 'folder_delete' | 'file_delete' | 'file_move' | 'folder_move';
  fileName: string;
  fileType?: string;
  timestamp: string;
  fileSize?: number | string;
  description: string;
}

interface RecentActivityProps {
  activities: ActivityItem[];
  maxItems?: number;
}

const RecentActivity: React.FC<RecentActivityProps> = ({ activities, maxItems = 10 }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Ensure activities is an array
  const activitiesArray = Array.isArray(activities) ? activities : [];

  const getActivityIcon = (type: string, fileType?: string) => {
    switch (type) {
      case 'upload':
        return <UploadIcon color="success" />;
      case 'download':
        return <DownloadIcon color="info" />;
      case 'share':
        return <ShareIcon color="warning" />;
      case 'delete':
        return <DeleteIcon color="error" />;
      case 'edit':
        return <EditIcon color="primary" />;
      case 'star':
        return <StarIcon color="warning" />;
      case 'unstar':
        return <StarBorderIcon color="action" />;
      case 'create_folder':
        return <FolderIcon color="primary" />;
      default:
        return getFileTypeIcon(fileType);
    }
  };

  const getFileTypeIcon = (fileType?: string) => {
    if (!fileType) return <FileIcon />;
    
    if (fileType.startsWith('image/')) return <ImageIcon />;
    if (fileType.startsWith('video/')) return <VideoIcon />;
    if (fileType.startsWith('audio/')) return <AudioIcon />;
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('tar')) return <ArchiveIcon />;
    
    return <FileIcon />;
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'upload':
        return theme.palette.success.main;
      case 'download':
        return theme.palette.info.main;
      case 'share':
        return theme.palette.warning.main;
      case 'delete':
        return theme.palette.error.main;
      case 'edit':
        return theme.palette.primary.main;
      case 'star':
        return theme.palette.warning.main;
      case 'unstar':
        return theme.palette.grey[500];
      case 'create_folder':
        return theme.palette.primary.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getActivityLabel = (type: string) => {
    switch (type) {
      case 'upload':
        return 'Uploaded';
      case 'download':
        return 'Downloaded';
      case 'share':
        return 'Shared';
      case 'delete':
        return 'Deleted';
      case 'edit':
        return 'Edited';
      case 'star':
        return 'Starred';
      case 'unstar':
        return 'Unstarred';
      case 'create_folder':
        return 'Created folder';
      case 'settings_update':
        return 'Updated settings';
      case 'avatar_upload':
        return 'Updated avatar';
      case 'avatar_delete':
        return 'Removed avatar';
      case 'folder_rename':
        return 'Renamed folder';
      case 'file_rename':
        return 'Renamed file';
      case 'folder_create':
        return 'Created folder';
      case 'folder_delete':
        return 'Deleted folder';
      case 'file_delete':
        return 'Deleted file';
      case 'file_move':
        return 'Moved file';
      case 'folder_move':
        return 'Moved folder';
      default:
        return 'Modified';
    }
  };

  const formatFileSize = (bytes?: number | string) => {
    if (!bytes) return '';
    
    // Convert string to number if needed
    const numBytes = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    
    if (isNaN(numBytes) || numBytes === 0) return '';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - activityTime.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return activityTime.toLocaleDateString();
  };

  const getDisplayFileName = (activity: ActivityItem) => {
    // For settings updates and other non-file activities, don't show file name
    if (activity.type === 'settings_update' || 
        activity.type === 'avatar_upload' || 
        activity.type === 'avatar_delete') {
      return '';
    }
    
    // For file/folder activities, show the file name or a default
    if (activity.fileName && activity.fileName !== 'Unknown') {
      return activity.fileName;
    }
    
    // For activities without specific file names, show generic text
    switch (activity.type) {
      case 'folder_rename':
        return 'Folder';
      case 'file_rename':
        return 'File';
      case 'folder_create':
        return 'New folder';
      case 'folder_delete':
        return 'Folder';
      case 'file_delete':
        return 'File';
      case 'file_move':
        return 'File';
      case 'folder_move':
        return 'Folder';
      default:
        return activity.fileName || 'Item';
    }
  };

  const displayedActivities = activitiesArray.slice(0, maxItems);

  return (
    <Card elevation={2} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              mr: 1 
            }} />
            Recent Activity
          </Typography>
          <Chip 
            label={`${activitiesArray.length} activities`} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        </Box>

        {displayedActivities.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <TimeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No recent activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your file activities will appear here
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {displayedActivities.map((activity, index) => (
              <ListItem
                key={activity.id}
                sx={{
                  px: 0,
                  py: 1.5,
                  borderBottom: index < displayedActivities.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: `${getActivityColor(activity.type)}20`,
                      color: getActivityColor(activity.type),
                      width: 40,
                      height: 40,
                    }}
                  >
                    {getActivityIcon(activity.type, activity.fileType)}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body1" component="div" sx={{ fontWeight: 500 }}>
                        {getActivityLabel(activity.type)}
                      </Typography>
                      {getDisplayFileName(activity) && (
                        <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
                          {getDisplayFileName(activity)}
                        </Typography>
                      )}
                      {activity.fileSize && activity.fileSize !== '0' && (
                        <Chip
                          label={formatFileSize(activity.fileSize)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" component="div" color="text.secondary">
                        {formatTimeAgo(activity.timestamp)}
                      </Typography>
                      {activity.description && activity.description !== `${activity.type} activity` && (
                        <>
                          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.secondary' }} />
                          <Typography variant="body2" component="div" color="text.secondary">
                            {activity.description}
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        {activitiesArray.length > maxItems && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/activity')}
            >
              View All Activities
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivity; 